/**
 * Coronis error toast that surfaces the backend's full, actionable error
 * (title + details + code/correlation-id) instead of a single terse line
 * (#4265 follow-up). Drop-in replacement for ra-commons `useApiErrorNotify`.
 *
 *   catch (error) { notifyError(error); }
 *
 * Renders, for a read-only partner write:
 *   Partner is read-only
 *   Write operations are not permitted for this partner. Select a writable partner to continue.
 *   PARTNER_READ_ONLY
 */
import { useNotify } from "react-admin";
import { useCallback } from "react";
import { parseRichError } from "@/utils/errors/richError";

export const useRichApiErrorNotify = (): ((error: unknown) => void) => {
  const notify = useNotify();
  return useCallback(
    (error: unknown) => {
      const rich = parseRichError(error);
      // Pass the structured error under `_aegle` so <AegleNotification> can render
      // title/details/code distinctly and offer a copy button; the title doubles
      // as the plain message for any default notification renderer.
      notify(rich.message, {
        type: rich.severity,
        multiLine: true,
        autoHideDuration: 10000,
        _aegle: rich,
      } as Parameters<typeof notify>[1]);
    },
    [notify]
  );
};
